import { useIvrMapVersions } from '../hooks/useIvrMapVersions';
import { useChartingRuns } from '../hooks/useChartingRuns';
import { usePayerDirectoryEntry } from '../hooks/usePayerDirectoryEntry';
import { representativeStatus, hasSubscriberDataPath } from '../utils/ivrGraphDisplay';
import styles from './PayerDiagnosticBanner.module.css';

// Surfaces the one thing worth knowing before relying on this payer's
// active map: whether a run has ever actually reached a human/queue.
// Grounded only in real computed data — no fabricated narrative about
// exactly where a call failed, since that isn't reliably derivable per run.
export function PayerDiagnosticBanner({ payerId }: { payerId: string }) {
  const { bundle } = useIvrMapVersions(payerId);
  const { runs } = useChartingRuns(payerId);
  const { entry, update } = usePayerDirectoryEntry(payerId);

  const graph = bundle?.active?.map ?? null;
  // 'reached' needs no banner (there's a confirmed path); 'unavailable' also
  // needs no banner — the payer has explicitly said no rep exists for this
  // call type, so "mark for manual charting" below would be bad advice
  // (manual charting can't find a path that structurally doesn't exist).
  // Only 'unresolved' — genuinely don't know yet — warrants this nudge.
  if (!graph || representativeStatus(graph) !== 'unresolved') return null;
  if (entry?.verificationStrategy === 'staff' || entry?.verificationStrategy === 'blocked')
    return null;

  const latestRun = runs[0] ?? null;

  const messageParts = ['No confirmed path to a representative yet.'];
  if (latestRun) {
    messageParts.push(
      `The last charting run ended with terminal state "${latestRun.terminalState}".`,
    );
  }
  if (!hasSubscriberDataPath(graph)) {
    messageParts.push(
      'No confirmed path to automated benefits either — more charting runs needed.',
    );
  }

  return (
    <div className={styles.banner}>
      <span className={styles.left}>
        <span>⚠</span>
        <span>{messageParts.join(' ')}</span>
      </span>
      <span
        className={styles.action}
        onClick={() => update.mutate({ verificationStrategy: 'staff' })}
      >
        Mark for manual charting
      </span>
    </div>
  );
}
